/**
 * Pure, React-free helpers for the Sprint Retro domain: id generation, blank
 * draft/record factories, validation, IMMUTABLE section mutators (add/remove
 * went-well lines, didn't-go-well items, per-item action items, and kudos),
 * roll-up helpers ({@link openActionItems} / {@link countsBySection}), and the
 * pointer-row index-attribute mapping used by {@link ./sprintRetroStore}.
 *
 * Keeping the mutators and the index-attribute mapping here — rather than inside
 * the store or the component — makes them unit-testable without the API facade
 * or React, and keeps the store a thin data-access wrapper. Mirrors
 * {@link ../../preplanning/prePlanModel} and {@link ../../taskTemplates/taskTemplateModel}.
 *
 * The large arrays (wentWell / didntGoWell / kudos) are NEVER mirrored onto the
 * pointer row — they live only in the S3 body — so the list renders from pointer
 * rows without hydrating S3.
 */

import { v4 as uuid } from 'uuid';
import type { StringMap } from '../../../api';
import type {
  RetroActionItem,
  RetroActionItemStatus,
  RetroSectionCounts,
  SprintRetro,
  SprintRetroDraft,
  SprintRetroSummary,
} from './types';

/** Logical collection name for the generic document store. */
export const SPRINT_RETROS_COLLECTION = 'sprintRetros';

/** The valid action-item statuses, in workflow order. */
export const RETRO_ACTION_ITEM_STATUSES: readonly RetroActionItemStatus[] = [
  'open',
  'in_progress',
  'done',
] as const;

/**
 * Deterministic document id for a sprint's retro. Keying by sprintId guarantees
 * a sprint has AT MOST ONE retro (a second save upserts the same document).
 */
export function retroDocId(sprintId: string): string {
  return `retro_${(sprintId ?? '').trim()}`;
}

/** Fresh id for a new went-well line. */
export function newWentWellId(): string {
  return `ww_${uuid()}`;
}

/** Fresh id for a new didn't-go-well item. */
export function newDidntGoWellId(): string {
  return `dg_${uuid()}`;
}

/** Fresh id for a new action item. */
export function newActionItemId(): string {
  return `ai_${uuid()}`;
}

/** Fresh id for a new kudos entry. */
export function newKudosId(): string {
  return `ku_${uuid()}`;
}

/** Creates an empty draft for a sprint (optionally seeding the sprint name). */
export function blankRetroDraft(
  sprintId: string,
  sprintName?: string,
): SprintRetroDraft {
  return {
    sprintId,
    sprintName,
    wentWell: [],
    didntGoWell: [],
    kudos: [],
  };
}

/** Projects an existing retro into an editable draft (deep-ish copy of arrays). */
export function draftFromRetro(retro: SprintRetro): SprintRetroDraft {
  return {
    sprintId: retro.sprintId,
    sprintName: retro.sprintName,
    wentWell: retro.wentWell.map((w) => ({ ...w })),
    didntGoWell: retro.didntGoWell.map((d) => ({
      ...d,
      actionItems: d.actionItems.map((a) => ({ ...a })),
    })),
    kudos: retro.kudos.map((k) => ({ ...k })),
  };
}

// ---------------------------------------------------------------------------
// Immutable section mutators. Each returns a NEW draft, never mutating input.
// ---------------------------------------------------------------------------

/** Adds a went-well line (defaults to blank text). */
export function addWentWell(
  draft: SprintRetroDraft,
  text = '',
): SprintRetroDraft {
  return {
    ...draft,
    wentWell: [...draft.wentWell, { id: newWentWellId(), text }],
  };
}

/** Removes a went-well line by id. */
export function removeWentWell(
  draft: SprintRetroDraft,
  id: string,
): SprintRetroDraft {
  return { ...draft, wentWell: draft.wentWell.filter((w) => w.id !== id) };
}

/** Updates a went-well line's text by id. */
export function updateWentWell(
  draft: SprintRetroDraft,
  id: string,
  text: string,
): SprintRetroDraft {
  return {
    ...draft,
    wentWell: draft.wentWell.map((w) => (w.id === id ? { ...w, text } : w)),
  };
}

/** Adds a didn't-go-well item (with an empty action-item list). */
export function addDidntGoWell(
  draft: SprintRetroDraft,
  text = '',
): SprintRetroDraft {
  return {
    ...draft,
    didntGoWell: [
      ...draft.didntGoWell,
      { id: newDidntGoWellId(), text, actionItems: [] },
    ],
  };
}

/** Removes a didn't-go-well item (and its action items) by id. */
export function removeDidntGoWell(
  draft: SprintRetroDraft,
  id: string,
): SprintRetroDraft {
  return {
    ...draft,
    didntGoWell: draft.didntGoWell.filter((d) => d.id !== id),
  };
}

/** Updates a didn't-go-well item's text by id. */
export function updateDidntGoWell(
  draft: SprintRetroDraft,
  id: string,
  text: string,
): SprintRetroDraft {
  return {
    ...draft,
    didntGoWell: draft.didntGoWell.map((d) =>
      d.id === id ? { ...d, text } : d,
    ),
  };
}

/** Adds an action item to a specific didn't-go-well item. */
export function addActionItem(
  draft: SprintRetroDraft,
  didntGoWellId: string,
  seed: Partial<Omit<RetroActionItem, 'id'>> = {},
): SprintRetroDraft {
  const item: RetroActionItem = {
    id: newActionItemId(),
    text: seed.text ?? '',
    owner: seed.owner ?? '',
    status: seed.status ?? 'open',
  };
  return {
    ...draft,
    didntGoWell: draft.didntGoWell.map((d) =>
      d.id === didntGoWellId
        ? { ...d, actionItems: [...d.actionItems, item] }
        : d,
    ),
  };
}

/** Removes an action item from a specific didn't-go-well item. */
export function removeActionItem(
  draft: SprintRetroDraft,
  didntGoWellId: string,
  actionItemId: string,
): SprintRetroDraft {
  return {
    ...draft,
    didntGoWell: draft.didntGoWell.map((d) =>
      d.id === didntGoWellId
        ? {
            ...d,
            actionItems: d.actionItems.filter((a) => a.id !== actionItemId),
          }
        : d,
    ),
  };
}

/** Patches an action item's fields (text / owner / status) in place. */
export function updateActionItem(
  draft: SprintRetroDraft,
  didntGoWellId: string,
  actionItemId: string,
  patch: Partial<Omit<RetroActionItem, 'id'>>,
): SprintRetroDraft {
  return {
    ...draft,
    didntGoWell: draft.didntGoWell.map((d) =>
      d.id === didntGoWellId
        ? {
            ...d,
            actionItems: d.actionItems.map((a) =>
              a.id === actionItemId ? { ...a, ...patch } : a,
            ),
          }
        : d,
    ),
  };
}

/** Adds a kudos entry (from defaults to the current user's alias). */
export function addKudos(
  draft: SprintRetroDraft,
  from: string,
  seed: Partial<Pick<SprintRetroDraft['kudos'][number], 'to' | 'message'>> = {},
): SprintRetroDraft {
  return {
    ...draft,
    kudos: [
      ...draft.kudos,
      {
        id: newKudosId(),
        to: seed.to ?? '',
        from,
        message: seed.message ?? '',
      },
    ],
  };
}

/** Removes a kudos entry by id. */
export function removeKudos(
  draft: SprintRetroDraft,
  id: string,
): SprintRetroDraft {
  return { ...draft, kudos: draft.kudos.filter((k) => k.id !== id) };
}

/** Patches a kudos entry's fields (to / message) by id. */
export function updateKudos(
  draft: SprintRetroDraft,
  id: string,
  patch: Partial<Pick<SprintRetroDraft['kudos'][number], 'to' | 'message'>>,
): SprintRetroDraft {
  return {
    ...draft,
    kudos: draft.kudos.map((k) => (k.id === id ? { ...k, ...patch } : k)),
  };
}

// ---------------------------------------------------------------------------
// Roll-ups.
// ---------------------------------------------------------------------------

/**
 * Flattens every action item across all "didn't go well" items whose status is
 * NOT 'done' — the retro's open follow-ups. Order preserves item then
 * action-item order.
 */
export function openActionItems(
  retro: Pick<SprintRetro, 'didntGoWell'>,
): RetroActionItem[] {
  const out: RetroActionItem[] = [];
  for (const item of retro.didntGoWell ?? []) {
    for (const action of item.actionItems ?? []) {
      if (action.status !== 'done') out.push(action);
    }
  }
  return out;
}

/** Per-section counts, including total and open action-item tallies. */
export function countsBySection(
  retro: Pick<SprintRetro, 'wentWell' | 'didntGoWell' | 'kudos'>,
): RetroSectionCounts {
  let actionItems = 0;
  let open = 0;
  for (const item of retro.didntGoWell ?? []) {
    for (const action of item.actionItems ?? []) {
      actionItems += 1;
      if (action.status !== 'done') open += 1;
    }
  }
  return {
    wentWell: (retro.wentWell ?? []).length,
    didntGoWell: (retro.didntGoWell ?? []).length,
    actionItems,
    openActionItems: open,
    kudos: (retro.kudos ?? []).length,
  };
}

// ---------------------------------------------------------------------------
// Cleaning + validation.
// ---------------------------------------------------------------------------

/**
 * Trims and drops empty entries from a draft: blank went-well lines, blank
 * didn't-go-well items (with no non-empty action items), blank action items,
 * and blank kudos are removed. Returns a normalized copy for save.
 */
export function cleanRetroDraft(draft: SprintRetroDraft): SprintRetroDraft {
  const wentWell = draft.wentWell
    .map((w) => ({ ...w, text: w.text.trim() }))
    .filter((w) => w.text);

  const didntGoWell = draft.didntGoWell
    .map((d) => {
      const actionItems = d.actionItems
        .map((a) => ({
          ...a,
          text: a.text.trim(),
          owner: a.owner.trim(),
        }))
        .filter((a) => a.text || a.owner);
      return { ...d, text: d.text.trim(), actionItems };
    })
    .filter((d) => d.text || d.actionItems.length > 0);

  const kudos = draft.kudos
    .map((k) => ({
      ...k,
      to: k.to.trim(),
      from: k.from.trim(),
      message: k.message.trim(),
    }))
    .filter((k) => k.to || k.message);

  return { ...draft, wentWell, didntGoWell, kudos };
}

/**
 * Validates a draft for save. A retro needs a sprint; each action item that has
 * text must name an owner; each kudos with a message must name a recipient.
 * Returns a list of human-readable problems (empty ⇒ valid). Run
 * {@link cleanRetroDraft} first so blank scaffolding rows never trip validation.
 */
export function validateRetroDraft(draft: SprintRetroDraft): string[] {
  const problems: string[] = [];
  if (!draft.sprintId.trim()) {
    problems.push('A sprint is required — pick the sprint this retro is for.');
  }
  draft.didntGoWell.forEach((d, di) => {
    d.actionItems.forEach((a, ai) => {
      if (a.text.trim() && !a.owner.trim()) {
        problems.push(
          `Item ${di + 1}, action ${ai + 1}: an owner is required.`,
        );
      }
    });
  });
  draft.kudos.forEach((k, ki) => {
    if (k.message.trim() && !k.to.trim()) {
      problems.push(`Kudos ${ki + 1}: a recipient (to) is required.`);
    }
  });
  return problems;
}

// ---------------------------------------------------------------------------
// Pointer-row index-attribute mapping.
// ---------------------------------------------------------------------------

/** Pointer-row index attribute keys (mirrored onto the DDB pointer row). */
export const SPRINT_RETRO_ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  sprintId: 'sprintId',
  sprintName: 'sprintName',
  createdBy: 'createdBy',
  createdAt: 'createdAt',
  lastUpdated: 'lastUpdated',
  deleted: 'deleted',
} as const;

/**
 * Builds the queryable index attributes mirrored onto the pointer row. The
 * large arrays (wentWell / didntGoWell / kudos) are intentionally NOT mirrored
 * (they live in the S3 body); the list view reconstructs summaries from these
 * attributes without hydrating S3.
 */
export function retroIndexAttributes(retro: SprintRetro): StringMap {
  return {
    [SPRINT_RETRO_ROW.sprintId]: retro.sprintId ?? '',
    [SPRINT_RETRO_ROW.sprintName]: retro.sprintName ?? '',
    [SPRINT_RETRO_ROW.createdBy]: retro.createdBy ?? '',
    [SPRINT_RETRO_ROW.createdAt]: retro.createdAt ?? '',
    [SPRINT_RETRO_ROW.lastUpdated]: retro.lastUpdated ?? '',
    [SPRINT_RETRO_ROW.deleted]: retro.deleted ? 'true' : 'false',
  };
}

/** Reconstructs a retro summary from a pointer row. */
export function retroSummaryFromRow(row: StringMap): SprintRetroSummary {
  return {
    id: row[SPRINT_RETRO_ROW.primaryIdentifier] ?? '',
    sprintId: row[SPRINT_RETRO_ROW.sprintId] ?? '',
    sprintName: row[SPRINT_RETRO_ROW.sprintName] || undefined,
    createdBy: row[SPRINT_RETRO_ROW.createdBy] ?? '',
    createdAt: row[SPRINT_RETRO_ROW.createdAt] ?? '',
    lastUpdated: row[SPRINT_RETRO_ROW.lastUpdated] ?? '',
  };
}
